/**
 * RFC 0345: a newly shared document the shared worker now holds.
 *
 * A tab's query for a document it was not yet let into ends `unavailable`
 * and stays that way: the worker keeps fetching it, says so when it lands
 * ("document-ready"), and a query that already gave up never asks again.
 * The invitee sat on "Waiting to be let in" with the session at the worker.
 * A hook bumps a counter for its id here, so its query is made again.
 */
import { useEffect, useState } from 'react'

const ready = new EventTarget()
const bare = (id: string) => id.replace(/^automerge:/, '')

export function noteDocumentReady(documentId: string): void {
  ready.dispatchEvent(new CustomEvent(bare(documentId)))
}

/** Changes each time the worker reports `documentId` ready. */
export function useDocumentReadyNonce(
  documentId: string | undefined | null,
): number {
  const [nonce, setNonce] = useState(0)
  useEffect(() => {
    if (!documentId) return
    const id = bare(String(documentId))
    const bump = () => setNonce((n) => n + 1)
    ready.addEventListener(id, bump)
    return () => ready.removeEventListener(id, bump)
  }, [documentId])
  return nonce
}
